import { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import SearchInput from "../../../utils/SearchInput";
import defaultImage from "../../../utils/DefaultImage";
import TableLoader from "../../../utils/TableLoader";
import PaginationComponent from "../../../utils/PaginationComponent";
import { FaPlus } from "react-icons/fa";
import { BiSolidTrashAlt } from "react-icons/bi";
import { TbEdit } from "react-icons/tb";
import { MdSaveAlt } from "react-icons/md";
import { RiEyeCloseLine, RiEyeLine } from "react-icons/ri";
import { motion, AnimatePresence } from "framer-motion";
import {
    containerVariants,
    itemVariants,
    cardVariants,
    buttonAnimations,
    dialogVariants,
} from "../../../utils/Animations";
import BeverageAdd from "./BeverageAdd";
import BeverageEdit from "./BeverageEdit";

const BeverageManagement = ((props, ref) => {

    const { rest_slug2 } = useParams();
    const navigate = useNavigate();
    const apiUrl = import.meta.env.VITE_METERI_API_BASE_URL;
    const logoUrl = import.meta.env.VITE_BACKEND_APP_STORAGE_URL;
    const token = localStorage.getItem("token");

    const [isLoading, setIsLoading] = useState({ main: false, edit: false });
    const [beverageData, setBeverageData] = useState({
        name: "",
        description: "",
        full_price: "",
    });

    const [logo, setLogo] = useState(null);
    const [selecteLogo, setSelecteLogo] = useState('');
    const [logoError, setLogoError] = useState('');

    const [beverageListData, setBeverageListData] = useState([]);
    const [currentBeverageData, setCurrentBeverageData] = useState(null);
    const [isAdd, setIsAdd] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [dialog, setDialog] = useState(false);
    const [selectedBeverageId, setSelectedBeverageId] = useState(null);

    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [showOption, setShowOption] = useState({ allergens: false, category: false });
    const [selectedCategory, setSelectedCategory] = useState([]);
    const [categoryData, setCategoryData] = useState([]);
    const [allergensData, setAllergensData] = useState([]);
    const [selectedAllergens, setSelectedAllergens] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [searchQuery, setSearchQuery] = useState({ allergens: "", category: "" });

    const [isChecked, setIsChecked] = useState(0);
    const [isMenu, setIsMenu] = useState(0);

    const handleSearchChange = (value) => {
        setSearchText(value);
    };

    const fetchCategoryData = async () => {
        try {
            const response = await axios.get(`${apiUrl}/api/v1/restaurant/${rest_slug2}/category`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { search: searchQuery.category, pagination: 0 },
            });
            setCategoryData(response?.data);
        } catch (error) {
            console.error("Error fetching allergens", error);
        }
    };

    const fetchAllergens = async () => {
        try {
            const response = await axios.get(`${apiUrl}/api/v1/get-allergens`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { search: searchQuery.allergens, pagination: 0 },
            });

            setAllergensData(response?.data?.data);
        } catch (error) {
            console.error("Error fetching allergens", error);
        }
    };

    const toggleDropdown = (key) => {
        setShowOption((prev) => ({
            allergens: key === "allergens" ? !prev.allergens : false,
            category: key === "category" ? !prev.category : false,
        }));
    };

    const handleCategorySelection = (categoryId) => {
        setSelectedCategory(categoryId);
        setTimeout(() => {
            toggleDropdown("category");
        }, 100);
    };

    const handleAllergenSelection = (allergenId) => {
        setSelectedAllergens((prev) =>
            prev.includes(allergenId) ? prev.filter((id) => id !== allergenId) : [...prev, allergenId]
        );
    };

    const getBeverageData = async () => {
        setIsLoading({ ...isLoading, main: true });
        try {
            let params;
            if (searchText) {
                params = {
                    pageNumber: page,
                    size: rowsPerPage,
                    search: searchText,
                };
            } else {
                params = {
                    pageNumber: page,
                    size: rowsPerPage,
                };
            }
            const response = await axios.get(`${apiUrl}/api/v1/restaurants/${rest_slug2}/beverages`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                params,
            });

            const data = response?.data;
            setBeverageListData(data?.data);
            setTotalPages(data?.total);
        } catch (error) {
            console.error('Error fetching order data:', error);
        } finally {
            setIsLoading({ ...isLoading, main: false });
        }
    };

    const getRestProfileData = async () => {
        try {
            const responseProfileData = await axios.get(`${apiUrl}/api/v1/restaurants/${rest_slug2}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setIsMenu(responseProfileData?.data?.is_show_menu || 0);
            setIsChecked(responseProfileData?.data?.is_show_beverages || 0);
        } catch (error) {
            console.error('Error fetching Profile Settings data:', error);
        }
    };

    const handleLogoChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            const maxSizeInBytes = 2 * 1024 * 1024; // 2MB
            if (file.size > maxSizeInBytes) {
                setLogoError('Max size: 2MB. Please upload a smaller file.');
                return;
            }

            const img = new Image();
            img.onload = () => {
                setLogoError('');
                const fileUrl = URL.createObjectURL(file);
                setLogo(fileUrl);
                setSelecteLogo(file);
            };

            img.onerror = () => {
                setLogoError('Invalid image file. Please select a valid image.');
            };

            img.src = URL.createObjectURL(file);
        } else {
            setLogoError('No file selected. Please choose a file to upload.');
        }
    };

    const changeBevarageShowStatus = async () => {
        const newStatus = isChecked ? 0 : 1;
        setIsChecked(!isChecked);
        try {
            const formData = new FormData();
            formData.append("restaurant_id", rest_slug2);
            formData.append("is_show_beverages", newStatus);
            formData.append("is_show_menu", isMenu);
            const response = await axios.post(
                `${apiUrl}/api/v1/section-status`,
                formData,
                {
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );
            getRestProfileData();
            toast.success(response?.data?.message, {
                theme: "colored",
                autoClose: 1000,
            });
        } catch (error) {
            console.error("Error updating menu status:", error);
        }
    };

    const handleSave = async () => {
        if (logoError) {
            toast.error(logoError, { theme: "colored", autoClose: 1000 });
            return;
        }
        setIsLoading({ ...isLoading, edit: true });
        try {
            if (!token) {
                console.error('No token found in localStorage.');
                return;
            }
            const formData = new FormData();
            formData.append("name", beverageData.name);
            if (selectedAllergens.length > 0) {
                selectedAllergens.forEach((allergenId) =>
                    formData.append("allergens[]", allergenId)
                );
            } else {
                formData.append("allergens", "");
            }
            formData.append("full_price", beverageData.full_price);
            formData.append("description", beverageData.description);
            if (selectedCategory) {
                formData.append("category_id", selectedCategory);
            } else {
                formData.append("category_id", "");
            }
            if (selecteLogo) {
                formData.append("picture", selecteLogo);
            } else if (logo === null) {
                formData.append("picture", "");
            }
            const response = await axios.post(
                `${apiUrl}/api/v1/beverages/${currentBeverageData.id}`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );
            getBeverageData();
            toast.success(response?.data?.message || "Beverage Updated Successfully!", {
                theme: "colored",
                autoClose: 1000,
            });
            setIsEditing(false);
            navigate(
                `/restaurantPanel/beverageManagement/${localStorage.getItem("currentRestId") &&
                localStorage.getItem("currentRestId")
                }`
            );
        } catch (error) {
            console.error("Error adding beverage:", error?.response?.data?.errors);
            const errors = error?.response?.data?.errors;
            if (errors) {
                for (const key in errors) {
                    if (Array.isArray(errors[key])) {
                        errors[key].forEach((message) => {
                            toast.error(message, {
                                theme: "colored",
                                autoClose: 3000,
                            });
                        });
                    }
                }
            } else {
                toast.error(error?.response?.data?.message, {
                    theme: "colored",
                    autoClose: 3000,
                });
            }
        } finally {
            setIsLoading({ ...isLoading, save: false });
        }
    };

    const handleDelete = async (id) => {
        if (!id) {
            console.error('Error: ID is required to delete a Beverage.');
            return;
        }
        setIsLoading({ ...isLoading, main: true });
        try {
            const response = await axios.delete(`${apiUrl}/api/v1/beverages/${id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            getBeverageData();
            toast.success(response?.data?.message || 'Beverage deleted successfully:', { theme: "colored", autoClose: 1000 });
        } catch (error) {
            console.error('Error deleting beverages:', error?.response?.data?.message || error.message);
            toast.error('Failed to delete the beverages. Please try again.', { theme: 'colored', autoClose: 3000 });
        } finally {
            setIsLoading({ ...isLoading, main: false });
        }
    };

    const handleEditClick = (data) => {
        setIsEditing(true);
        setCurrentBeverageData(data);
        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    };

    const handleCancel = () => {
        setIsEditing(false);
        setIsAdd(false);
        setCurrentBeverageData(null);
    };

    const deleteProfile = () => {
        setSelecteLogo('');
        setLogo(null);
    };

    useImperativeHandle(ref, () => ({
        beverage() {
            setIsEditing(false);
            setIsAdd(false);
            setCurrentBeverageData(null);
        },
    }));

    useEffect(() => {
        setSearchQuery({ allergens: "", category: "" });
    }, [showOption]);


    useEffect(() => {
        fetchCategoryData();
    }, [searchQuery.category]);

    useEffect(() => {
        fetchAllergens();
    }, [searchQuery.allergens]);

    useEffect(() => {
        if (currentBeverageData) {
            setBeverageData({
                name: currentBeverageData?.name || "",
                full_price: currentBeverageData?.full_price || "",
                description: currentBeverageData?.description || "",
            });

            const allergenIds = currentBeverageData?.allergens?.map((allergen) => allergen.id) || [];
            setSelectedAllergens(allergenIds);

            if (currentBeverageData?.category?.length > 0) {
                setSelectedCategory(currentBeverageData?.category[0]?.id);
            }

            const completeLogoUrl = currentBeverageData?.picture
                ? `${logoUrl}/${currentBeverageData?.picture}`
                : null;
            setLogo(completeLogoUrl);
        }
    }, [currentBeverageData]);

    useEffect(() => {
        getBeverageData();
        getRestProfileData();
    }, [page, rowsPerPage, searchText]);

    useEffect(() => {
        const handlePageRefresh = () => {
            localStorage.removeItem("searchTextUser");
        };
        window.addEventListener("beforeunload", handlePageRefresh);
        return () => {
            window.removeEventListener("beforeunload", handlePageRefresh);
        };
    }, []);

    return (
        <>
            <AnimatePresence mode="wait">
                {isAdd ? (
                    <motion.div
                        key="add-beverage"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                    >
                        <BeverageAdd
                            setIsAdd={setIsAdd}
                            getBeverageData={getBeverageData}
                        />
                    </motion.div>
                ) : isEditing ? (
                    <motion.div
                        key="edit-beverage"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                    >
                        <BeverageEdit
                            logo={logo}
                            setLogo={setLogo}
                            logoError={logoError}
                            setLogoError={setLogoError}
                            handleLogoChange={handleLogoChange}
                            beverageData={beverageData}
                            setBeverageData={setBeverageData}
                            isLoading={isLoading}
                            setIsLoading={setIsLoading}
                            deleteProfile={deleteProfile}

                            showOption={showOption}
                            setShowOption={setShowOption}
                            toggleDropdown={toggleDropdown}
                            searchQuery={searchQuery}
                            setSearchQuery={setSearchQuery}
                            categoryData={categoryData}
                            selectedCategory={selectedCategory}
                            handleCategorySelection={handleCategorySelection}
                            allergensData={allergensData}
                            selectedAllergens={selectedAllergens}
                            handleAllergenSelection={handleAllergenSelection}
                            handleSave={handleSave}
                            handleCancel={handleCancel}
                        />
                    </motion.div>

                ) : (
                    <motion.div
                        key="beverage-list"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        className="space-y-6"
                    >
                        <motion.section
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                            className="border-t border-[#D9D9D9] sm:mt-5 pt-5"
                        >
                            <motion.div
                                className="flex xl:flex-row flex-col justify-between items-center mt-3 mb-3"
                                variants={containerVariants}
                            >
                                <motion.div
                                    className="flex items-center justify-between gap-x-2 xl:w-auto w-full"
                                    variants={itemVariants}
                                >
                                    <motion.h3
                                        className="text-3xl font-bold mb-2 poppins-medium self-start"
                                        variants={itemVariants}
                                    >
                                        Gestisci Bevande
                                    </motion.h3>
                                    <div className="flex items-center space-x-2 sm:hidden">
                                        <motion.label
                                            {...buttonAnimations}
                                            className="cursor-pointer"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={changeBevarageShowStatus}
                                                className="sr-only peer"
                                            />
                                            <div className="relative w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full after:absolute after:top-[2px] after:start-[2px] after:bg-white after:h-4 after:w-4 after:rounded-full after:transition-all peer-checked:bg-[#8B2433]">
                                            </div>
                                        </motion.label>
                                        <span className="text-sm font-medium poppins-regular w-20 text-left">
                                            {isChecked ? "Visibile" : "Nascosto"}
                                        </span>
                                    </div>
                                </motion.div>
                                <motion.div
                                    className="self-start flex items-center gap-2 sm:flex-row flex-col sm:w-auto w-full"
                                    variants={itemVariants}
                                >
                                    <motion.div variants={itemVariants}>
                                        <SearchInput
                                            onSearchChange={handleSearchChange}
                                            component="Beverage Management"
                                            placeholder="Bevanda"
                                        />
                                    </motion.div>
                                    <motion.button
                                        className="flex sm:w-auto w-full items-center justify-center px-2 py-1 bg-[#8A2533] text-white rounded-lg border-2 border-[#953543] shadow-sm hover:bg-[#8A2533] transition-all duration-200 group"
                                        onClick={() => setIsAdd(true)}
                                        {...buttonAnimations}
                                    >
                                        <FaPlus />
                                        <span className="ml-2 poppins-regular">
                                            Aggiungi Bevanda
                                        </span>
                                    </motion.button>
                                    <div className="sm:flex items-center space-x-2 hidden">
                                        <motion.label
                                            className="cursor-pointer inline-block"
                                            {...buttonAnimations}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={changeBevarageShowStatus}
                                                className="sr-only peer"
                                            />
                                            <div className="relative w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full after:absolute after:top-[2px] after:start-[2px] after:bg-white after:h-4 after:w-4 after:rounded-full after:transition-all peer-checked:bg-[#8B2433]">
                                            </div>
                                        </motion.label>
                                        <motion.span
                                            variants={itemVariants}
                                            key={isChecked ? 'visible' : 'hidden'}
                                        >
                                            {isChecked ? <RiEyeLine size={25} /> : <RiEyeCloseLine size={25} />}
                                        </motion.span>
                                        {/* <motion.span
                                            {...buttonAnimations}
                                            className="hover:bg-[#8A2533] rounded-full p-1 group"
                                        >
                                            <MdSaveAlt
                                                size={25}
                                                className="cursor-pointer group-hover:invert transition duration-100"
                                                onClick={() => {
                                                    window.open(`/beverage_list_pdf/${rest_slug2}`, "_blank");
                                                }}
                                            />
                                        </motion.span> */}
                                    </div>
                                    <motion.span
                                            {...buttonAnimations}
                                            className="hover:bg-[#8A2533] rounded-full p-1 group"
                                        >
                                            <MdSaveAlt
                                                size={25}
                                                className="cursor-pointer group-hover:invert transition duration-100"
                                                onClick={() => {
                                                    window.open(`/beverage_list_pdf/${rest_slug2}`, "_blank");
                                                }}
                                            />
                                        </motion.span>
                                </motion.div>
                            </motion.div>
                            <motion.div
                                className="user-mangement-main w-auto h-auto bg-[#FBFBFB] border-[2px] border-[#8B24332B] px-6 pt-3 pb-0 rounded-xl shadow"
                                variants={cardVariants}
                            >
                                <table className="w-full text-left wine_table">
                                    <thead>
                                        <motion.tr
                                            className="border-b border-[#8B2433]"
                                            variants={itemVariants}
                                        >
                                            <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Immagine</th>
                                            <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Nome</th>
                                            <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Descrizione</th>
                                            <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Allergeni</th>
                                            <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Prezzo</th>
                                            <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Opzioni</th>
                                        </motion.tr>
                                    </thead>
                                    {isLoading.main ? (
                                        <TableLoader />
                                    ) : (
                                        <tbody>
                                            {beverageListData && beverageListData?.length > 0 ? (
                                                beverageListData?.map((beverage, index) => (
                                                    <motion.tr
                                                        key={index}
                                                        className="border-b border-[#8B2433]/12"
                                                        variants={itemVariants}
                                                        custom={index}
                                                    >
                                                        <td className="pr-2 py-4">
                                                            <motion.div
                                                                className="thumbnail_image rounded-lg w-[50px] h-[60px] p-1 flex align-center justify-center"
                                                                {...buttonAnimations}
                                                            >
                                                                <img
                                                                    className="w-10 h-10 rounded-full"
                                                                    src={
                                                                        beverage?.picture && beverage?.picture !== ""
                                                                            ? `${logoUrl}/${beverage?.picture}`
                                                                            : defaultImage
                                                                    }
                                                                    alt={beverage?.name}
                                                                />
                                                            </motion.div>
                                                        </td>
                                                        <td className="py-4 px-2 text-left poppins-medium text-[#303030]">
                                                            {beverage?.name?.replace(/\s+/g, ' ') || "N/A"}
                                                        </td>
                                                        <td className="py-4 px-2 text-left poppins-medium text-[#303030] max-w-[200px] truncate">
                                                            {beverage?.description?.replace(/\s+/g, ' ') || "-"}
                                                        </td>
                                                        <td className="py-4 px-2 text-left poppins-medium text-[#303030] relative group cursor-pointer">
                                                            {beverage?.allergens?.length > 0 ? (
                                                                <motion.div
                                                                    className="relative group inline-block"
                                                                    variants={itemVariants}
                                                                >
                                                                    <span>{beverage.allergens[0].name_it}</span>
                                                                    {beverage.allergens.length > 1 && (
                                                                        <motion.div
                                                                            className="absolute tool-tips-hover bottom-full left-0 mb-2 hidden group-hover:block bg-gray-800 text-white text-sm rounded px-2 py-1"
                                                                            variants={itemVariants}
                                                                        >
                                                                            {beverage.allergens.slice(1).map((allergen, idx) => (
                                                                                <span key={idx}>
                                                                                    {allergen.name_it}
                                                                                    {idx < beverage.allergens.length - 2 && ', '}
                                                                                </span>
                                                                            ))}
                                                                        </motion.div>
                                                                    )}
                                                                </motion.div>
                                                            ) : (
                                                                <span>-</span>
                                                            )}
                                                        </td>
                                                        <td className="py-4 px-2 text-left poppins-medium text-[#303030]">
                                                            {beverage?.full_price ? `${beverage?.full_price} €` : "N/A"}
                                                        </td>
                                                        <td className="py-4 px-2">
                                                            <motion.button
                                                                className="text-red-700 mx-2"
                                                                onClick={() => handleEditClick(beverage)}
                                                                {...buttonAnimations}
                                                            >
                                                                <TbEdit size={"20px"} />
                                                            </motion.button>
                                                            <motion.button
                                                                className="text-red-700"
                                                                onClick={() => { setDialog(true); setSelectedBeverageId(beverage?.id); }}
                                                                {...buttonAnimations}
                                                            >
                                                                <BiSolidTrashAlt size={"18px"} />
                                                            </motion.button>
                                                            <AnimatePresence>
                                                                {dialog && (
                                                                    <motion.div
                                                                        className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
                                                                        variants={dialogVariants}
                                                                        initial="hidden"
                                                                        animate="visible"
                                                                        exit="exit"
                                                                    >
                                                                        <motion.div
                                                                            className="bg-white rounded-lg shadow-lg sm:w-1/3 w-[280px]"
                                                                            variants={dialogVariants}
                                                                        >
                                                                            <div className="px-6 py-4 border-b">
                                                                                <h3 className="text-lg font-semibold text-gray-800 poppins-medium">
                                                                                    Are you sure you want to delete the beverage?
                                                                                </h3>
                                                                            </div>
                                                                            <div className="px-6 py-4 flex justify-end space-x-4">
                                                                                <motion.button
                                                                                    onClick={() => { handleDelete(selectedBeverageId); setDialog(false); }}
                                                                                    className="px-4 py-2 poppins-regular bg-[#8A2533] text-white rounded-md hover:bg-[#f6efef] hover:text-[#8A2533] transition-colors"
                                                                                    {...buttonAnimations}
                                                                                >
                                                                                    Yes
                                                                                </motion.button>
                                                                                <motion.button
                                                                                    onClick={() => setDialog(false)}
                                                                                    className="px-4 py-2 poppins-regular bg-gray-300 text-gray-700 rounded-md hover:text-white hover:bg-[#8A2533] transition-colors"
                                                                                    {...buttonAnimations}
                                                                                >
                                                                                    No
                                                                                </motion.button>
                                                                            </div>
                                                                        </motion.div>
                                                                    </motion.div>
                                                                )}
                                                            </AnimatePresence>
                                                        </td>
                                                    </motion.tr>
                                                ))
                                            ) : (
                                                <motion.tr variants={itemVariants}>
                                                    <td colSpan={9} className="text-center py-4">No Beverage found</td>
                                                </motion.tr>
                                            )}
                                        </tbody>
                                    )}
                                </table>
                            </motion.div>
                            {beverageListData && beverageListData?.length > 0 && (
                                <motion.div variants={itemVariants}>
                                    <PaginationComponent
                                        page={page}
                                        totalPages={totalPages}
                                        rowsPerPage={rowsPerPage}
                                        setPage={setPage}
                                        setRowsPerPage={setRowsPerPage}
                                    />
                                </motion.div>
                            )}
                        </motion.section>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
});

export default forwardRef(BeverageManagement);